import { useCallback } from 'react'
import { useSubscription } from '../../../../../store'
import { usePaywallState } from '../../../../../store/paywalls'
import { useShowPaywall } from '@renderer/components/pricing/use-show-paywall'
import { MentionableItem } from '../../../../../types'
import { useCreditsTopUp } from '../../../../../hooks/use-credits-top-up'
import { isLegacyPricingPlan } from '../../../../../utils/billingUtils'

type ModalIntent = { modal: 'paywall'; feature: 'premium-responses' | 'highlights' } | { modal: 'credits' }

/** Which mentions cost money: a pro model, a pro integration, or Highlights. */
const isPaidMention = (mention: MentionableItem) =>
  (mention.type === 'model' && mention.pricing?.isFree === false) ||
  (mention.type === 'backend_plugin' && (mention.isPro === true || mention.value === 'search_highlights'))

/**
 * The single paywall decision for a pick. Highlights is pro-or-nothing; the
 * metered features (pro models, pro integrations) additionally stop at the
 * usage limit, where a plan that can buy credits does, and a trial is asked to
 * upgrade. `canBuyCredits` is the tier check rather than a plan-name test, so a
 * paid v2 subscriber gets the purchase path the server-side refusal expects
 * them to have, and a legacy plan gets nothing.
 */
const paywallIntentFor = (
  mention: MentionableItem,
  state: { isFree: boolean; isTrial: boolean; isLegacy: boolean; canBuyCredits: boolean; atLimit: boolean },
): ModalIntent | null => {
  if (!isPaidMention(mention)) return null

  const isHighlights = mention.type === 'backend_plugin' && mention.value === 'search_highlights'
  if (state.isFree) {
    return { modal: 'paywall', feature: isHighlights ? 'highlights' : 'premium-responses' }
  }
  // Pro users (trial or paid) can use Highlights without a usage check.
  if (isHighlights || !state.atLimit) return null
  if (state.canBuyCredits) return { modal: 'credits' }
  // eat reaches here as a trial, because the server presents it as 'pro-trial'.
  // The backend enforces no credit limit on a legacy plan, so refusing the pick
  // would block a model the server would serve (HIG-6548).
  if (state.isLegacy) return null
  if (state.isTrial) return { modal: 'paywall', feature: 'premium-responses' }
  return null
}

/**
 * Gates a pick on the user's plan: when `mention` costs what they can't spend,
 * opens the paywall or the credits purchase and returns true, so the caller
 * drops the pick.
 */
export const usePaidMentionGate = () => {
  const showPaywall = useShowPaywall()
  const { tier: creditsTier, openCreditsPurchase } = useCreditsTopUp()
  const paywallState = usePaywallState()
  const { subscription } = useSubscription()

  return useCallback(
    (mention: MentionableItem, from: string): boolean => {
      const intent = paywallIntentFor(mention, {
        isFree: paywallState.plan === 'free',
        isTrial: subscription.isTrial === true,
        isLegacy: isLegacyPricingPlan(paywallState.plan),
        // Excludes trials the way the paywall does: a trial resolves the tier
        // it converts to, so without this the upgrade branch above never runs.
        canBuyCredits: creditsTier !== null && subscription.isTrial !== true,
        atLimit: paywallState.atLimit,
      })
      if (!intent) {
        return false
      }
      if (intent.modal === 'credits') {
        openCreditsPurchase(from)
      } else {
        showPaywall('credits')
      }
      return true
    },
    [paywallState, subscription, creditsTier, openCreditsPurchase, showPaywall],
  )
}
